import { useEffect, useState } from "react";

type ThemeMode = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

function getStoredTheme(): ThemeMode {
  const value = localStorage.getItem("theme");
  return value === "light" || value === "dark" || value === "system" ? value : "system";
}

function resolveTheme(theme: ThemeMode): ResolvedTheme {
  if (theme === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }

  return theme;
}

export function useThemeManager() {
  const [theme, setTheme] = useState<ThemeMode>(getStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(getStoredTheme()));

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const applyTheme = () => {
      const nextTheme = getStoredTheme();
      const resolved = resolveTheme(nextTheme);
      setTheme(nextTheme);
      setResolvedTheme(resolved);
      document.documentElement.classList.toggle("dark", resolved === "dark");
      document.documentElement.setAttribute("data-theme", resolved);
    };

    applyTheme();

    const handleThemeEvent = () => applyTheme();
    const handleMediaChange = () => {
      if (theme === "system") {
        applyTheme();
      }
    };

    window.addEventListener("serid-theme-change", handleThemeEvent);
    mediaQuery.addEventListener("change", handleMediaChange);

    return () => {
      window.removeEventListener("serid-theme-change", handleThemeEvent);
      mediaQuery.removeEventListener("change", handleMediaChange);
    };
  }, [theme]);

  return resolvedTheme;
}
